import "./focus.component.scss";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { FiCheck } from "react-icons/fi";
import { IoArrowForward } from "react-icons/io5";
import { BsFullscreen, BsFullscreenExit, BsArrowClockwise, BsArrowsMove, BsX, BsChevronUp } from "react-icons/bs";
import { changeFocus, watchFocus, completionKey, createFocusCommands, focusEmoji, FOCUS_DEFAULTS, remainingSeconds, phaseDuration, formatFocusTime, toMillis } from "../../utils/focusSession";

const EMPTY_ITEMS = [];

// Edit these four phrases here. Generic focus uses the task phrases.
const PHRASES = {
  task: { focus: "let’s make progress", break: "recharge" },
  habit: { focus: "ants keep the rhythm", break: "you’ve earned it" },
};

function StepButton({ direction, disabled, onChange }) {
  const timer = useRef(null), held = useRef(false), lastClick = useRef(0);
  const cancel = () => clearTimeout(timer.current);
  useEffect(() => cancel, []);
  return <button type="button" disabled={disabled} className="ant-focus-step"
    title="Click for 1 minute; 2x click or hold for 5 minutes" aria-label={direction > 0 ? "Increase duration" : "Decrease duration"}
    onContextMenu={(e) => e.preventDefault()}
    onPointerDown={(e) => {
      if (e.button !== 0) return;
      held.current = false;
      timer.current = setTimeout(() => { held.current = true; lastClick.current = 0; onChange(direction * 300); }, 450);
    }} onPointerUp={cancel} onPointerLeave={cancel} onPointerCancel={cancel}
    onClick={(e) => {
      if (held.current) { held.current = false; return; }
      const now = Date.now();
      const double = lastClick.current > 0 && e.detail !== 0 &&
        (e.detail % 2 === 0 || now - lastClick.current < 320);
      // The first click already added one minute. Add four more, making five total.
      onChange(direction * (double ? 240 : 60));
      lastClick.current = double || e.detail === 0 ? 0 : now;
    }}>{direction > 0 ? "+" : "−"}</button>;
}

function StaticRing({ progress, size = 36, clockWise = true, children }) {
  const p = Math.max(0, Math.min(1, progress)), c = 2 * Math.PI * 46;
  return <div className="ant-focus-static-ring" style={{ width: size, height: size }}>
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="2" />
      {p > 0 && <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeDasharray={`${c * p} ${c}`} strokeDashoffset={clockWise ? -c * (1 - p) : 0} transform="rotate(-90 50 50)" />}
    </svg>{children}
  </div>;
}

// One local tick in the visible clock; the task list and settings stay still.
const FocusClock = memo(function FocusClock({ a, foreground, emoji, centerContent, size = 180,
  mini = false, showTime = false, showTitle = false, hidePhrase = false, showIcon = true, clockWise = true }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (a?.state !== "running" || !foreground) return;
    const timer = setInterval(() => {
      tick((n) => n + 1);
      if (remainingSeconds(a) <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [a?.state, a?.targetEndAt, foreground]);
  const remaining = remainingSeconds(a), duration = phaseDuration(a);
  const phrase = PHRASES[a?.taskRef?.type === "habit" ? "habit" : "task"][a?.mode === "break" ? "break" : "focus"];
  const glyph = centerContent || (emoji ? <span>{emoji}</span> : <FiCheck aria-hidden="true" />);
  return <>
    <StaticRing progress={mini && !a ? 0 : remaining / duration} size={size} clockWise={clockWise}>
      {mini ? showIcon && <span className="ant-focus-mini-glyph" style={{ fontSize: Number(size) * 0.47 }}>{glyph}</span>
        : <div className="ant-focus-center">
          <span className="ant-focus-task-icon">{glyph}</span>
          <span>{a?.mode === "break" ? "Take a break" : "Focus"}</span>
          <strong>{formatFocusTime(remaining)}</strong>
          {!hidePhrase && <small>{phrase}</small>}
        </div>}
    </StaticRing>
    {mini && showTime && <span className="ant-focus-mini-label"><strong>{formatFocusTime(remaining)}</strong>
      {showTitle && <small>{a?.mode === "break" ? "Break" : a?.taskRef?.title || a?.taskRef?.name || "something"}</small>}
    </span>}
  </>;
});

export default function FocusApp({ centerContent, showIcon = true, clockWise = true }) {
  const tasks = useSelector((s) => s.tasks.tasks) || EMPTY_ITEMS;
  const projectTasks = useSelector((s) => s.prjtasks?.prjtasks);
  const allTasks = useMemo(() => [...new Map([...tasks, ...(projectTasks || [])].map((t) =>
    [`${t.fromSpaceId}/${t.fromStationId}/${t.id}`, t])).values()], [tasks, projectTasks]);
  const spaces = useSelector((s) => s.space.spaceData) || EMPTY_ITEMS;
  const habits = useSelector((s) => s.habits.habits) || EMPTY_ITEMS;
  const uid = useSelector((s) => s.user.currentUser?.uid);
  const [confirmed, setFocus] = useState(null);
  const [preview, setPreview] = useState(null);
  const a = preview?.value ?? confirmed;
  const stations = useSelector((s) => s.space.stationData) || EMPTY_ITEMS;
  const [online, setOnline] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState("hidden");
  const [settings, setSettings] = useState(false);
  const [picker, setPicker] = useState(false);
  const [search, setSearch] = useState("");
  const [foreground, setForeground] = useState(!document.hidden);
  const [wake, setWake] = useState(0);
  const [, tick] = useState(0);
  const [controls, setControls] = useState(true);
  const [scheduledTask, setScheduledTask] = useState(null);
  const [point, setPoint] = useState({ x: 24, y: 120 });
  const pointer = useRef(null);
  const snapshotCounter = useRef(0);
  const activeUid = useRef(uid); activeUid.current = uid;
  const lastEvent = useRef(null), notifiedPhase = useRef(null), seenTasks = useRef(new Set());
  const latest = useRef(confirmed); latest.current = confirmed;
  const visible = useRef(a); visible.current = a;
  const sounds = useRef({});
  const reduceMotion = useReducedMotion();
  const immersive = view === "immersive", compact = view === "compact";
  const options = { ...FOCUS_DEFAULTS, ...a?.options };
  const running = a?.state === "running";
  const task = a?.taskRef;
  const emoji = focusEmoji(task, stations);
  const title = task?.title || task?.name || "something";

  useEffect(() => {
    if (!uid) return;
    let disposed = false;
    latest.current = null; setFocus(null); setPreview(null); setOnline(false); lastEvent.current = null; seenTasks.current.clear();
    const off = watchFocus(uid, (data, connected) => {
      if (disposed) return;
      if (lastEvent.current === null) lastEvent.current = data?.event?.id || "initial";
      snapshotCounter.current++;
      latest.current = data; setFocus(data); setOnline(connected);
    }, (e) => { if (!disposed) setError(e.message); });
    return () => { disposed = true; off(); };
  }, [uid]);
  useEffect(() => {
    const visibility = () => { setForeground(!document.hidden); if (!document.hidden) setWake((n) => n + 1); };
    document.addEventListener("visibilitychange", visibility);
    const resize = () => setPoint((p) => ({ x: Math.max(12, Math.min(window.innerWidth - 76, p.x)), y: Math.max(12, Math.min(window.innerHeight - 76, p.y)) }));
    window.addEventListener("resize", resize);
    return () => { document.removeEventListener("visibilitychange", visibility); window.removeEventListener("resize", resize); };
  }, []);
  useEffect(() => {
    const a = confirmed;
    if (!uid || !online || !foreground || a?.state !== "running") return;
    let disposed = false, timer;
    const finish = async () => {
      const snapshotAtStart = snapshotCounter.current;
      try {
        const result = await changeFocus(uid, "complete", {}, completionKey(a));
        if (!disposed) {
          if (snapshotCounter.current === snapshotAtStart) { latest.current = result.next; setFocus(result.next); }
          setError("");
        }
      } catch (e) {
        if (!disposed) { setError("Timer finished. Reconnect to save it, or use Retry."); timer = setTimeout(finish, 30000); }
      }
    };
    timer = setTimeout(finish, Math.max(0, remainingSeconds(a) * 1000 + 50));
    return () => { disposed = true; clearTimeout(timer); };
  }, [uid, confirmed?.phaseId, confirmed?.state, confirmed?.targetEndAt, online, foreground, wake]);

  const play = (mode) => {
    if (!latest.current?.options?.sound) return;
    const audio = sounds.current[mode];
    if (audio) { audio.currentTime = 0; audio.play().catch(() => {}); }
  };
  useEffect(() => {
    sounds.current = { focus: new Audio("/media/mixkit-magic-astral-sweep-effect-2629.wav"), break: new Audio("/media/mixkit-intro-transition-1146.wav") };
    Object.values(sounds.current).forEach((audio) => { audio.preload = "auto"; audio.volume = 0.55; });
    return () => { Object.values(sounds.current).forEach((audio) => { audio.pause(); audio.src = ""; }); sounds.current = {}; };
  }, []);
  useEffect(() => {
    const a = confirmed;
    const event = a?.event;
    if (!event || lastEvent.current === event.id) return;
    lastEvent.current = event.id;
    if (!foreground || Date.now() - event.at > 10000) return;
    if (!event.late) play(event.mode);
    if (event.kind === "complete" && options.reminders && !/INPUT|TEXTAREA/.test(document.activeElement?.tagName || "") && !document.activeElement?.isContentEditable) {
      setView((v) => v === "hidden" || v === "floating" ? "compact" : v);
      setControls(true);
    }
  }, [confirmed?.event?.id, foreground]);

  // Best-effort desktop notification while this tab remains open. Browsers may
  // throttle background timers; a closed tab does not run this client code.
  useEffect(() => {
    const a = confirmed;
    if (!a || a.state !== "running" || !options.reminders) return;
    const timer = setTimeout(() => {
      if (!document.hidden || notifiedPhase.current === a.phaseId || !("Notification" in window) || Notification.permission !== "granted") return;
      notifiedPhase.current = a.phaseId;
      try {
        const n = new Notification(a.mode === "focus" ? "Time for a break" : "Ready to focus?", {
          body: "Open your focus timer to continue.", tag: `ant-focus-${uid}`, silent: !options.sound,
        });
        n.onclick = () => { window.focus(); setView("compact"); n.close(); };
      } catch (_) { /* Some mobile browsers require an existing service worker. */ }
    }, Math.max(0, remainingSeconds(a) * 1000));
    return () => clearTimeout(timer);
  }, [confirmed?.phaseId, confirmed?.targetEndAt, confirmed?.state, options.reminders, options.sound, uid]);
  useEffect(() => {
    if (!immersive || !options.keepAwake || !foreground || !navigator.wakeLock) return;
    let disposed = false, lock;
    navigator.wakeLock.request("screen").then((value) => {
      lock = value; if (disposed) lock.release().catch(() => {});
    }).catch(() => {});
    return () => { disposed = true; lock?.release().catch(() => {}); };
  }, [immersive, options.keepAwake, foreground]);
  useEffect(() => {
    if (!immersive || !controls) return;
    const timer = setTimeout(() => setControls(false), 5000);
    return () => clearTimeout(timer);
  }, [immersive, controls]);
  useEffect(() => {
    if (!immersive) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const key = (e) => { if (e.key === "Escape") setView("standard"); };
    document.addEventListener("keydown", key);
    return () => { document.body.style.overflow = previous; document.removeEventListener("keydown", key); };
  }, [immersive]);

  // Preserve scheduled-task reminders, without silently replacing a running task.
  useEffect(() => {
    const check = () => {
      if (document.hidden) return;
      const now = Date.now();
      const t = tasks.find((task) => {
        const start = toMillis(task.startdate), key = `${task.id}-${start}`;
        return start && now >= start && now - start < 60000 && !seenTasks.current.has(key);
      });
      if (t) {
        seenTasks.current.add(`${t.id}-${toMillis(t.startdate)}`); setScheduledTask(t);
        if (!latest.current?.state || latest.current.state !== "running") setView("standard");
      }
    };
    check(); const timer = setInterval(check, 30000);
    return () => clearInterval(timer);
  }, [tasks]);

  const client = useMemo(() => createFocusCommands({
    uid, read: () => latest.current,
    commit: (next, before) => {
      if (activeUid.current === uid && latest.current === before) { latest.current = next; setFocus(next); }
    },
    preview: setPreview, error: (message) => setError(message || ""),
  }), [uid]);
  useEffect(() => { client.attach(); return () => client.detach(); }, [client]);
  const run = client.run;
  const primary = () => {
    const current = visible.current;
    return run(current?.state === "running" ? remainingSeconds(current) <= 0 ? "complete" : "pause" : "start");
  };
  const select = (selected) => {
    setPicker(false);
    void run("select", { task: selected ? { ...selected, focusIcon: focusEmoji(selected, stations) } : null });
  };
  const groups = useMemo(() => {
    const quickId = `${uid}-Inbox`;
    const result = [{ name: "Habits", id: "habits", items: habits.map((h) => ({ ...h, type: "habit" })) },
      { name: "Quick Tasks", id: quickId, items: allTasks.filter((t) => !t.fromSpaceId || t.fromSpaceId === quickId) }];
    [...new Set(allTasks.map((t) => t.fromSpaceId).filter((id) => id && id !== quickId))].forEach((id) => {
      result.push({ id, name: spaces.find((s) => s.spaceId === id)?.name || "Shared workspace", items: allTasks.filter((t) => t.fromSpaceId === id) });
    });
    const q = search.trim().toLowerCase();
    return result.map((g) => ({ ...g, items: g.items.filter((t) => (t.title || t.name || "").toLowerCase().includes(q)) }));
  }, [allTasks, habits, spaces, uid, search]);
  const icon = (node, action, label) => <button type="button" onClick={action} title={label} aria-label={label} className="ant-focus-icon">{node}</button>;

  if (!uid) return null;
  return createPortal(<div className={`ant-focus ${immersive ? "ant-focus-dark" : ""}`}>
    {view === "hidden" && <button type="button" className="ant-focus-launcher" onClick={() => setView("standard")} title="Open focus timer">
      <FocusClock a={a} foreground={foreground} emoji={emoji} showIcon={showIcon} clockWise={clockWise} mini showTime size={26} />
    </button>}
    {view === "floating" && <button type="button" className="ant-focus-floating" style={{ left: point.x, top: point.y }} aria-label="Open focus timer; drag to move"
      onPointerDown={(e) => { if (e.button !== 0) return; e.currentTarget.setPointerCapture(e.pointerId); pointer.current = { x: e.clientX, y: e.clientY, base: point, moved: false }; }}
      onPointerMove={(e) => {
        const p = pointer.current; if (!p || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
        const dx = e.clientX - p.x, dy = e.clientY - p.y;
        if (Math.abs(dx) + Math.abs(dy) > 5) p.moved = true;
        if (p.moved) setPoint({ x: Math.max(12, Math.min(window.innerWidth - 76, p.base.x + dx)), y: Math.max(12, Math.min(window.innerHeight - 76, p.base.y + dy)) });
      }}
      onPointerUp={() => { if (pointer.current?.moved) setPoint((p) => ({ ...p, x: p.x < window.innerWidth / 2 ? 12 : window.innerWidth - 76 })); }}
      onClick={(e) => { if (e.detail === 0 || !pointer.current?.moved) setView("standard"); }}>
      <FocusClock a={a} foreground={foreground} emoji={emoji} showIcon={showIcon} clockWise={clockWise} mini size={54} />
    </button>}
    {view !== "hidden" && view !== "floating" && <motion.section layout
      transition={{ duration: reduceMotion ? 0 : 0.25 }} className={`ant-focus-panel ant-focus-${view}`} aria-label="Focus timer"
      onPointerDown={() => setControls(true)} onKeyDown={() => setControls(true)}>
      <div className={`ant-focus-toolbar ${immersive && !controls ? "ant-focus-controls-hidden" : ""}`}>
        {icon(<BsX />, () => setView("hidden"), "Hide timer")}
        <div>{!compact && icon(<BsArrowsMove />, () => setView("floating"), "Float timer")}
          {!immersive && !compact && icon(<span>−</span>, () => setView("compact"), "Compact view")}
          {icon(immersive ? <BsFullscreenExit /> : compact ? <BsChevronUp /> : <BsFullscreen />,
            () => setView(immersive || compact ? "standard" : "immersive"), immersive || compact ? "Standard view" : "Immersive view")}</div>
      </div>
      <div className="ant-focus-scroll">
      {picker && !compact && !immersive ? <div className="ant-focus-picker">
        <div className="ant-focus-row"><h3>Focus on</h3><button type="button" className="ant-focus-generic" aria-pressed={!task} onClick={() => select(null)}>something</button>{icon(<BsX />, () => setPicker(false), "Close picker")}</div>
        <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks and habits" aria-label="Search tasks and habits" />
        <p className="ant-focus-hint">Switching saves current focus progress and prepares the selected item.</p>
        {groups.map((g) => <details key={g.id} open><summary>{g.name}</summary>
          {!g.items.length && <p className="ant-focus-hint">No items</p>}
          {g.items.map((t) => <div className="ant-focus-picker-item" key={`${t.fromStationId || ""}/${t.id}`}>
            <button type="button" onClick={() => select(t)}>
              <span className="ant-focus-picker-glyph">{focusEmoji(t, stations) || <FiCheck aria-hidden="true" />}</span>
              <span>{t.title || t.name}</span>
            </button>
            <Link className="ant-focus-open-item" aria-label={`Open ${t.title || t.name}`} title="Open item"
              to={t.type === "habit" ? `/?habit=${t.id}` : `/task/${t.id}`} onClick={() => setView("hidden")}><IoArrowForward /></Link>
          </div>)}</details>)}
      </div> : <>
        {compact ? <div className="ant-focus-compact-content">
          <button type="button" onClick={() => setView("standard")} className="ant-focus-compact-title"><FocusClock a={a} foreground={foreground} emoji={emoji} showIcon={showIcon} clockWise={clockWise} mini showTime showTitle size={42} /></button>
          <button type="button"  onClick={primary}>{running ? "Pause" : "Start"}</button>
        </div> : <>
          <motion.div layout className="ant-focus-ring-stage" transition={{ duration: reduceMotion ? 0 : 0.25 }}>
            <FocusClock a={a} foreground={foreground} emoji={emoji} centerContent={centerContent}
              size={immersive ? "min(72vw, 320px)" : 180} hidePhrase={immersive} clockWise={clockWise} />
          </motion.div>
          <div className="ant-focus-selected-task">
            <button type="button" disabled={immersive} onClick={() => setPicker(true)}>{title}</button>
            {task && <Link className="ant-focus-open-item" aria-label="Open selected item" title="Open selected item"
              to={task.type === "habit" ? `/?habit=${task.id}` : `/task/${task.id}`}
              onClick={() => setView("hidden")}><IoArrowForward /></Link>}
          </div>
          {immersive && <div className={`ant-focus-actions ${!controls ? "ant-focus-controls-hidden" : ""}`}>
            <button type="button" className="ant-focus-primary" onClick={primary}>{running ? "Pause" : "Start"}</button>
            {icon(<BsArrowClockwise />, () => run("reset"), "Save progress and reset")}
          </div>}
        </>}
        {!compact && !immersive && <>
          <div className="ant-focus-actions">{["focus", "break"].map((mode) => <button type="button" key={mode}
            aria-pressed={(a?.mode || "focus") === mode} onClick={() => run("mode", { mode })} className="ant-focus-tab">{mode === "focus" ? "Focus" : "Break"}</button>)}</div>
          <div className="ant-focus-lengths">{["focus", "break"].map((mode) => <div key={mode}>
            <label>{mode === "focus" ? "Focus minutes" : "Break minutes"}</label>
            <div><StepButton direction={-1} disabled={running} onChange={(delta) => run("length", { mode, delta })} />
              <strong>{Math.round((mode === "focus" ? a?.sessionMs ?? 1500 : a?.breakMs ?? 300) / 60)}</strong>
              <StepButton direction={1} disabled={running} onChange={(delta) => run("length", { mode, delta })} /></div>
          </div>)}</div>
          <p className="ant-focus-hint">Click ±1 minute · 2x click ±5 minutes</p>
          <button type="button" className="ant-focus-settings-button" onClick={() => setSettings((v) => !v)}>{settings ? "Hide settings" : "Timer settings"}</button>
          {settings && <div className="ant-focus-settings">
            {[["autoBreak", "Start breaks automatically"], ["autoFocus", "Start next focus automatically"], ["sound", "Transition sounds"],
              ["reminders", "Show phase reminders"], ["keepAwake", "Keep immersive display awake"]].map(([key, label]) => <label key={key}>
              {label}<input type="checkbox" checked={!!options[key]} disabled={key === "keepAwake" && !navigator.wakeLock} onChange={(e) => run("options", { [key]: e.target.checked })} />
            </label>)}
            <label>Automatic focus cycles<select value={options.cycleLimit} onChange={(e) => run("options", { cycleLimit: +e.target.value })}>
              <option value={0}>Unlimited</option><option value={4}>4</option><option value={8}>8</option></select></label>
            {"Notification" in window && Notification.permission !== "granted" && <button type="button" onClick={() => Notification.requestPermission().then(() => tick((n) => n + 1)).catch(() => setError("Desktop alerts are unavailable in this browser."))}>Enable desktop alerts</button>}
          </div>}
        </>}
      </>}
      {!online && <p className="ant-focus-hint">Offline or connecting · changes need a connection</p>}

      {scheduledTask && !immersive && <div className="ant-focus-scheduled"><span>Scheduled: {scheduledTask.title}</span>
        <button type="button" onClick={() => { select(scheduledTask); setScheduledTask(null); }}>Focus on this</button>
        {icon(<BsX />, () => setScheduledTask(null), "Dismiss reminder")}</div>}
      </div>
      {error && <p className="ant-focus-error" role="status">{error} <button type="button" onClick={() => run(visible.current?.state === "running" && remainingSeconds(visible.current) <= 0 ? "complete" : "refresh", {})}>Retry</button></p>}
      {!compact && !immersive && !picker && <motion.div layout="position" className="ant-focus-footer">
        <button type="button" className="ant-focus-primary" onClick={primary}>{running ? "Pause" : "Start"}</button>
        {icon(<BsArrowClockwise />, () => run("reset"), "Save progress and reset")}
      </motion.div>}
    </motion.section>}
  </div>, document.body);
}
